import React, { useEffect, useId, useRef } from "react";
import { subscribeCardMotion } from "../../native/cardMotion";
import { normalizeAttitude } from "../../utils/cardMotionMath";
import { createCardMotionCalibration } from "../../utils/cardMotionCalibration";
import "../../styles/RatingCardMotion.css";

// Match the native sensor rate, including on 120Hz ProMotion displays.
const FRAME_INTERVAL = 1000 / 30;
const SETTLE_EPSILON = 0.002;
const FOIL_CONTROLS = ".vg-rating-pill--premium, .vg-rating-identity-chip--premium, .vg-rating-action-surface--premium, .vg-rating-skill-pill--premium";

export default function RatingCardMotion({ active = true, children, className = "", ...props }) {
  const cardRef = useRef(null);
  const rimId = useId().replace(/:/g, "");

  useEffect(() => {
    const card = cardRef.current;
    if (!card || !active) return undefined;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let unsubscribe;
    const calibration = createCardMotionCalibration();
    let sensorActive = false;
    let raf = 0;
    let lastFrame = null;
    let staleTimer;
    let drag = null;
    let sensorTarget = null;
    let suppressClickUntil = 0;
    let target = { x: 0, y: 0, strength: 0 };
    let rendered = { ...target };
    const paintedStyles = new WeakMap();
    const angle = () => window.screen?.orientation?.angle ?? window.orientation ?? 0;

    function setStyle(element, property, value) {
      if (!element) return;
      let previous = paintedStyles.get(element);
      if (!previous) {
        previous = {};
        paintedStyles.set(element, previous);
      }
      if (previous[property] === value) return;
      if (property.startsWith("--")) element.style.setProperty(property, value);
      else element.style[property] = value;
      previous[property] = value;
    }
    function paint() {
      const { x, y, strength } = rendered;
      // Keep updates local to these layers. Inherited CSS variables previously
      // invalidated every premium gradient/control on every animation frame.
      setStyle(card, "transform", `perspective(1200px) rotateX(${(-y * 11).toFixed(3)}deg) rotateY(${(x * 14).toFixed(3)}deg)`);
      setStyle(card.querySelector(".vg-card-castle-background"), "transform", `translate3d(${(x * 8).toFixed(2)}px, ${(-y * 10).toFixed(2)}px, 0)`);
      const light = card.querySelector(".vg-card-reflection__light");
      setStyle(light, "transform", `translate3d(${(-x * 24).toFixed(2)}%, ${(y * 18).toFixed(2)}%, 0)`);
      const premium = card.classList.contains("vg-rating-card-shell--premium");
      const travel = Math.min(1, Math.hypot(x, y));
      setStyle(light, "opacity", (strength * (premium ? .12 + travel * .09 : .086 + travel * .072)).toFixed(3));
      // Two fixed SVG strokes crossfade for the rim. The avatar's small foil
      // disk and each control's bounded sheen move as prepainted surfaces;
      // none of their gradient geometry changes with the sensor.
      setStyle(card.querySelector(".vg-card-rim-light"), "opacity", (strength * Math.max(0, Math.min(1, .45 + x * .35 - y * .25))).toFixed(3));
      const avatar = card.querySelector(".vg-rating-avatar-frame--premium");
      setStyle(avatar, "--card-avatar-turn", `${(x * 55 - y * 35).toFixed(2)}deg`);
      card.querySelectorAll(FOIL_CONTROLS).forEach((control) => {
        setStyle(control, "--card-control-shift", `${(x * 50 - y * 30).toFixed(2)}%`);
      });
    }
    function frame(now) {
      if (lastFrame !== null && now - lastFrame < FRAME_INTERVAL - 1) {
        raf = requestAnimationFrame(frame);
        return;
      }
      const dt = lastFrame !== null ? Math.min(now - lastFrame, 50) : FRAME_INTERVAL;
      lastFrame = now;
      const ease = 1 - Math.exp(-dt / 105);
      let moving = false;
      for (const key of ["x", "y", "strength"]) {
        if (Math.abs(target[key] - rendered[key]) <= SETTLE_EPSILON) rendered[key] = target[key];
        else {
          rendered[key] += (target[key] - rendered[key]) * ease;
          moving = true;
        }
      }
      paint();
      if (moving) raf = requestAnimationFrame(frame);
      else raf = 0;
    }
    function update(next) {
      target = next;
      const changed = Object.keys(next).some((key) => Math.abs(next[key] - rendered[key]) > SETTLE_EPSILON);
      if (!raf && changed) raf = requestAnimationFrame(frame);
    }
    function reset() {
      calibration.reset();
      sensorActive = false;
      sensorTarget = null;
      clearTimeout(staleTimer);
      if (drag) return;
      card.removeAttribute("data-motion-active");
      update({ x: 0, y: 0, strength: 0 });
    }
    function receive(sample) {
      const attitude = normalizeAttitude(sample);
      if (!attitude) { reset(); return; }
      sensorActive = true;
      sensorTarget = { ...calibration.sample(attitude, performance.now(), angle(), Boolean(drag)), strength: 1 };
      if (!drag) {
        card.dataset.motionActive = "true";
        update(sensorTarget);
      }
      clearTimeout(staleTimer);
      staleTimer = setTimeout(reset, 1200);
    }
    function sync() {
      unsubscribe?.();
      unsubscribe = undefined;
      cancelDrag();
      reset();
      // Hidden/covered cards must release their animation work immediately.
      cancelAnimationFrame(raf);
      raf = 0;
      lastFrame = null;
      target = rendered = { x: 0, y: 0, strength: 0 };
      paint();
      card.dataset.motionEnabled = String(!document.hidden && !reducedMotion.matches);
      if (!document.hidden && !reducedMotion.matches) unsubscribe = subscribeCardMotion(receive);
    }
    function pointerMove(event) {
      if (drag) {
        if (event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.clientX;
        const dy = event.clientY - drag.clientY;
        if (!drag.moved && Math.hypot(dx, dy) < 5) return;
        drag.moved = true;
        if (event.cancelable) event.preventDefault();
        card.dataset.motionActive = "true";
        card.dataset.motionDragging = "true";
        const clamp = (value) => Math.max(-1, Math.min(1, value));
        update({ x: clamp(drag.x + dx / drag.travel), y: clamp(drag.y - dy / drag.travel), strength: 1 });
        return;
      }
      if (sensorActive || event.pointerType !== "mouse" || reducedMotion.matches) return;
      // Use the untransformed layout box, avoiding feedback from the tilt itself.
      const bounds = card.parentElement.getBoundingClientRect();
      const x = Math.max(-1, Math.min(1, (event.clientX - bounds.left) / bounds.width * 2 - 1));
      const y = Math.max(-1, Math.min(1, (event.clientY - bounds.top) / bounds.height * 2 - 1));
      card.dataset.motionActive = "true";
      update({ x, y: -y, strength: 1 });
    }
    function pointerDown(event) {
      if (drag || event.isPrimary === false || event.button !== 0 || reducedMotion.matches || document.hidden) return;
      suppressClickUntil = 0;
      if (event.target.closest('button, a, input, textarea, select, [role="button"], [role="slider"], [contenteditable="true"], [data-card-no-drag]')) return;
      drag = {
        id: event.pointerId, clientX: event.clientX, clientY: event.clientY,
        x: rendered.x, y: rendered.y, moved: false,
        travel: Math.max(100, card.getBoundingClientRect().width * .48),
      };
      card.setPointerCapture?.(event.pointerId);
    }
    function cancelDrag() {
      if (!drag) return;
      const id = drag.id;
      drag = null;
      card.removeAttribute("data-motion-dragging");
      if (card.hasPointerCapture?.(id)) card.releasePointerCapture(id);
    }
    function pointerUp(event) {
      if (!drag || drag.id !== event.pointerId) return;
      if (drag.moved) suppressClickUntil = performance.now() + 350;
      cancelDrag();
      if (sensorTarget) update(sensorTarget);
      else reset();
    }
    function preventDragClick(event) {
      if (performance.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
    function pointerLeave() { if (!drag && !sensorActive) reset(); }
    function preventImageDrag(event) { event.preventDefault(); }
    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("orientationchange", sync);
    window.screen?.orientation?.addEventListener("change", sync);
    reducedMotion.addEventListener("change", sync);
    card.addEventListener("pointermove", pointerMove);
    card.addEventListener("pointerdown", pointerDown);
    card.addEventListener("pointerup", pointerUp);
    card.addEventListener("pointercancel", pointerUp);
    card.addEventListener("lostpointercapture", pointerUp);
    card.addEventListener("click", preventDragClick, true);
    card.addEventListener("dragstart", preventImageDrag);
    card.addEventListener("pointerleave", pointerLeave);
    return () => {
      unsubscribe?.();
      cancelDrag();
      clearTimeout(staleTimer);
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("orientationchange", sync);
      window.screen?.orientation?.removeEventListener("change", sync);
      reducedMotion.removeEventListener("change", sync);
      card.removeEventListener("pointermove", pointerMove);
      card.removeEventListener("pointerdown", pointerDown);
      card.removeEventListener("pointerup", pointerUp);
      card.removeEventListener("pointercancel", pointerUp);
      card.removeEventListener("lostpointercapture", pointerUp);
      card.removeEventListener("click", preventDragClick, true);
      card.removeEventListener("dragstart", preventImageDrag);
      card.removeEventListener("pointerleave", pointerLeave);
      card.removeAttribute("data-motion-active");
      card.removeAttribute("data-motion-enabled");
      card.style.removeProperty("transform");
      card.querySelector(".vg-card-castle-background")?.style.removeProperty("transform");
      const light = card.querySelector(".vg-card-reflection__light");
      light?.style.removeProperty("transform");
      light?.style.removeProperty("opacity");
      card.querySelector(".vg-card-rim-light")?.style.removeProperty("opacity");
      card.querySelector(".vg-rating-avatar-frame--premium")?.style.removeProperty("--card-avatar-turn");
      card.querySelectorAll(FOIL_CONTROLS).forEach((control) => control.style.removeProperty("--card-control-shift"));
    };
  }, [active]);

  return (
    <div {...props} ref={cardRef} className={`vg-card-motion ${className}`}>
      <div className="vg-card-motion-clip">
        {children}
        <div className="vg-card-reflection" aria-hidden="true">
          <div className="vg-card-reflection__light" />
        </div>
        <div className="vg-card-reflection__edge" aria-hidden="true">
          <svg className="vg-card-rim" width="100%" height="100%">
            <defs><linearGradient id={`${rimId}-foil`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgb(var(--vg-premium-soft))" />
              <stop offset="24%" stopColor="rgb(var(--vg-premium-main))" />
              <stop offset="48%" stopColor="rgb(var(--vg-premium-deep))" />
              <stop offset="72%" stopColor="rgb(var(--vg-premium-accent))" />
              <stop offset="100%" stopColor="rgb(var(--vg-premium-soft))" />
            </linearGradient></defs>
            <rect className="vg-card-rim-stroke" x="1.5" y="1.5" rx="24.5" fill="none" stroke={`url(#${rimId}-foil)`} strokeWidth="2.5" />
          </svg>
          <svg className="vg-card-rim vg-card-rim-light" width="100%" height="100%">
            <defs><linearGradient id={`${rimId}-light`} x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgb(var(--vg-premium-soft))" stopOpacity="0" />
              <stop offset="24%" stopColor="white" stopOpacity=".95" />
              <stop offset="52%" stopColor="rgb(var(--vg-premium-soft))" stopOpacity=".15" />
              <stop offset="78%" stopColor="white" stopOpacity=".85" />
              <stop offset="100%" stopColor="rgb(var(--vg-premium-soft))" stopOpacity="0" />
            </linearGradient></defs>
            <rect className="vg-card-rim-stroke" x="1.5" y="1.5" rx="24.5" fill="none" stroke={`url(#${rimId}-light)`} strokeWidth="2.5" />
          </svg>
        </div>
      </div>
    </div>
  );
}
